import React, { useState } from "react";
import ReactDOM from "react-dom";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import UserInfo from "@utils/UserInfo";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import closeIcon from "@icons/ic-close-white.svg";
import {
  Overlay,
  DialogContainer,
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  RequiredMessage,
  PasswordWrapper,
  PasswordLabel,
  PasswordInputContainer,
  PasswordInput,
  PasswordError,
  PasswordToggle,
} from "./MyAccount.styled";

const MyAccount = (props) => {
  const { t, userDetails, onClose, onUpdateSuccess } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: {
      required: true,
      value: userDetails?.firstName ?? userDetails?.first_name ?? "",
      error: "",
    },
    lastName: {
      required: true,
      value: userDetails?.lastName ?? userDetails?.last_name ?? "",
      error: "",
    },
    job: {
      required: false,
      value: userDetails?.job ?? "",
      error: "",
    },
    phone: {
      required: false,
      value: userDetails?.phone ?? "",
      error: "",
    },
    newPassword: {
      required: false,
      value: "",
      error: "",
    },
    passwordConfirmation: {
      required: false,
      value: "",
      error: "",
    },
  });

  const showPasswordConfirmation = formData.newPassword.value.length > 0;

  const onFieldChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: {
        ...formData[field],
        value: e.target.value,
        error: "",
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    const newData = { ...data };

    if (!newData.firstName.value.trim()) {
      isFormValid = false;
      newData.firstName.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!newData.lastName.value.trim()) {
      isFormValid = false;
      newData.lastName.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (newData.newPassword.value) {
      if (newData.newPassword.value.length < 8) {
        isFormValid = false;
        newData.newPassword.error = t("RESET_PASSWORD_ERROR_MIN_LENGTH");
      } else if (!newData.passwordConfirmation.value) {
        isFormValid = false;
        newData.passwordConfirmation.error = t("GENERAL_MANDATORY_ERROR");
      } else if (
        newData.newPassword.value !== newData.passwordConfirmation.value
      ) {
        isFormValid = false;
        newData.passwordConfirmation.error = t("RESET_PASSWORD_ERROR_MISMATCH");
      }
    }

    if (!isFormValid) {
      setFormData(newData);
    }

    return isFormValid;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (!isFormValid) return;

    setIsSubmitting(true);
    setApiError("");

    const payload = {
      firstname: formData.firstName.value,
      lastname: formData.lastName.value,
      job: formData.job.value,
      phone: formData.phone.value,
    };

    if (formData.newPassword.value) {
      payload.password = btoa(formData.newPassword.value);
    }

    const userId = userDetails?.id;

    axios
      .put(`/general/admins/${userId}`, payload)
      .then((response) => {
        setIsSubmitting(false);
        // Update localStorage with new user info
        const parsed = UserInfo.getUser();
        if (parsed) {
          try {
            const updatedAdmin =
              response.data?.admins?.[0] || response.data?.admin || {};
            parsed.firstName =
              updatedAdmin.firstName || formData.firstName.value;
            parsed.lastName = updatedAdmin.lastName || formData.lastName.value;
            parsed.job = updatedAdmin.job || formData.job.value;
            parsed.phone = updatedAdmin.phone || formData.phone.value;
            localStorage.setItem("user_info", JSON.stringify(parsed));
          } catch (e) {
            // ignore
          }
        }
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        onClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        setApiError(error.response?.data?.message ?? t("GENERAL_ERROR"));
      });
  };

  const displayName = `${
    formData.firstName.value ||
    userDetails?.firstName ||
    userDetails?.first_name ||
    ""
  } ${
    formData.lastName.value ||
    userDetails?.lastName ||
    userDetails?.last_name ||
    ""
  }`.trim();

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <DialogContainer onClick={(e) => e.stopPropagation()}>
        <HeaderWrapper>
          <Title>{displayName || t("MENU_MY_ACCOUNT")}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt={t("CLOSE")}
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="myaccount_firstName"
                      type="text"
                      label={t("ADMIN_FIRSTNAME")}
                      isFloat
                      required
                      value={String(formData.firstName.value)}
                      onChange={onFieldChange("firstName")}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ fontStyle: "italic" }}
                      labelStyles={
                        formData.firstName.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.firstName.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.firstName.error}
                      placeholder={t("ADMIN_FIRSTNAME")}
                    />
                    <TextInput
                      id="myaccount_lastName"
                      type="text"
                      label={t("ADMIN_LASTNAME")}
                      isFloat
                      required
                      value={String(formData.lastName.value)}
                      onChange={onFieldChange("lastName")}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ fontStyle: "italic" }}
                      labelStyles={
                        formData.lastName.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.lastName.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.lastName.error}
                      placeholder={t("ADMIN_LASTNAME")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="myaccount_job"
                      type="text"
                      label={t("ADMIN_JOB_TITLE")}
                      isFloat
                      value={String(formData.job.value)}
                      onChange={onFieldChange("job")}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ fontStyle: "italic" }}
                      labelStyles={labelStyles}
                      inputStyles={inputStyles}
                      placeholder={t("ADMIN_JOB_TITLE")}
                    />
                    <TextInput
                      id="myaccount_phone"
                      type="text"
                      label={t("ADMIN_PHONE")}
                      isFloat
                      value={String(formData.phone.value)}
                      onChange={onFieldChange("phone")}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ fontStyle: "italic" }}
                      labelStyles={labelStyles}
                      inputStyles={inputStyles}
                      placeholder={t("ADMIN_PHONE")}
                    />
                  </GridRow>
                  <GridRow
                    column={showPasswordConfirmation ? "1fr 1fr" : "1fr"}
                  >
                    <PasswordWrapper>
                      <PasswordLabel
                        hasError={formData.newPassword.error !== ""}
                      >
                        {t("ADMIN_NEW_PASSWORD")}
                      </PasswordLabel>
                      <PasswordInputContainer>
                        <PasswordInput
                          id="myaccount_newPassword"
                          type={showPassword ? "text" : "password"}
                          value={String(formData.newPassword.value)}
                          onChange={onFieldChange("newPassword")}
                          placeholder={t("ADMIN_NEW_PASSWORD")}
                          hasError={formData.newPassword.error !== ""}
                        />
                        <PasswordToggle
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i
                            className={
                              showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                            }
                          />
                        </PasswordToggle>
                      </PasswordInputContainer>
                      {formData.newPassword.error && (
                        <PasswordError>
                          {formData.newPassword.error}
                        </PasswordError>
                      )}
                    </PasswordWrapper>
                    {showPasswordConfirmation && (
                      <PasswordWrapper>
                        <PasswordLabel
                          hasError={formData.passwordConfirmation.error !== ""}
                        >
                          {t("ADMIN_PASSWORD_CONFIRMATION")}*
                        </PasswordLabel>
                        <PasswordInputContainer>
                          <PasswordInput
                            id="myaccount_passwordConfirmation"
                            type={showPassword ? "text" : "password"}
                            value={String(formData.passwordConfirmation.value)}
                            onChange={onFieldChange("passwordConfirmation")}
                            placeholder={t("ADMIN_PASSWORD_CONFIRMATION")}
                            hasError={
                              formData.passwordConfirmation.error !== ""
                            }
                          />
                          <PasswordToggle
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <i
                              className={
                                showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                              }
                            />
                          </PasswordToggle>
                        </PasswordInputContainer>
                        {formData.passwordConfirmation.error && (
                          <PasswordError>
                            {formData.passwordConfirmation.error}
                          </PasswordError>
                        )}
                      </PasswordWrapper>
                    )}
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {apiError && <RequiredMessage>{apiError}</RequiredMessage>}
          <Button
            styles={{
              color: "#333",
              background: "#ccc",
              border: "1px solid #ccc",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#bbb",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isSubmitting && <ClipLoader size={15} color="#fff" />}
              {!isSubmitting && t("VALIDATE")}
            </>
          </Button>
        </ButtonWrapper>
      </DialogContainer>
    </Overlay>,
    document.body
  );
};

MyAccount.propTypes = {
  t: PropTypes.func.isRequired,
  userDetails: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdateSuccess: PropTypes.func,
};

MyAccount.defaultProps = {
  onUpdateSuccess: null,
};

export default withTranslation()(MyAccount);
